import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  Collapse,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  VStack,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Icon,
  useBreakpointValue,
  Card,
  CardBody,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  SimpleGrid,
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { 
  FaUsers, 
  FaMobileAlt, 
  FaHeadset, 
  FaChartLine, 
  FaDownload,
  FaEye,
  FaSync,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaDollarSign,
  FaCreditCard,
  FaStore,
  FaChartBar,
  FaFilePdf
} from "react-icons/fa";
import { MdTrendingUp, MdTrendingDown } from "react-icons/md";
import API from "../../context/API";
import { useNavigate } from 'react-router-dom';

// Import jsPDF for PDF generation
import jsPDF from 'jspdf';

function AdminReports() {
  const navigate = useNavigate();
  const [totalMerchants, setTotalMerchants] = useState(0);
  const [activeDevices, setActiveDevices] = useState(0);
  const [supportTickets, setSupportTickets] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [merchants, setMerchants] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Enhanced data states
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    conversionRate: 0,
    averageOrderValue: 0,
    customerSatisfaction: 0,
    systemUptime: 0
  });
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    end: new Date()
  });
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  // Enhanced color scheme
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const primaryColor = useColorModeValue("teal.500", "teal.400");
  const successColor = useColorModeValue("green.500", "green.400");
  const warningColor = useColorModeValue("orange.500", "orange.400");
  const dangerColor = useColorModeValue("red.500", "red.400");
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const selectedBg = useColorModeValue("blue.50", "blue.900");

  // Responsive values
  const headingSize = useBreakpointValue({ base: "lg", md: "xl", lg: "2xl" });
  const subheadingSize = useBreakpointValue({ base: "md", md: "lg" });
  const bodyTextSize = useBreakpointValue({ base: "sm", md: "md" });
  const metricValueSize = useBreakpointValue({ base: "2xl", md: "3xl", lg: "4xl" });

  const toast = useToast();

  useEffect(() => {
    fetchMetrics();
    
    // Set up real-time refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchMetrics();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(refreshInterval);
  }, []);

  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch all data in parallel with comprehensive endpoints
      const [
        merchantsResponse,
        devicesResponse,
        ordersResponse,
        revenueResponse,
        transactionsResponse,
        performanceResponse,
        supportResponse
      ] = await Promise.all([
        API.get("merchant").catch(() => ({ data: { data: [] } })),
        API.get("devices").catch(() => ({ data: { data: [] } })),
        API.get("orders").catch(() => ({ data: { data: [] } })),
        API.get("analytics/admin/revenue").catch(() => ({ data: { total: 0 } })),
        API.get("analytics/admin/transactions").catch(() => ({ data: { data: [] } })),
        API.get("analytics/admin/performance").catch(() => ({ data: { 
          conversionRate: 0,
          averageOrderValue: 0,
          customerSatisfaction: 0,
          systemUptime: 0
        } })),
        API.get("analytics/admin/support").catch(() => ({ data: { tickets: 0 } }))
      ]);

      // Process merchants data - ONLY REAL DATA
      const merchantsData = merchantsResponse.data.data || [];
      setTotalMerchants(merchantsData.length);
      setMerchants(merchantsData);

      // Process devices data - ONLY REAL DATA
      const devicesData = devicesResponse.data.data || [];
      setActiveDevices(devicesData.length);
      setDevices(devicesData);

      // Process orders data - ONLY REAL DATA
      const ordersData = ordersResponse.data.data || [];
      setTotalOrders(ordersData.length);

      // Process revenue data - ONLY REAL DATA
      const revenueData = revenueResponse.data.total || 0;
      setTotalRevenue(revenueData);

      // Process recent transactions - ONLY REAL DATA
      const transactionsData = transactionsResponse.data.data || [];
      setRecentTransactions(transactionsData.slice(0, 10));

      // Process performance metrics - ONLY REAL DATA
      const performanceData = performanceResponse.data || {};
      setPerformanceMetrics({
        conversionRate: performanceData.conversionRate || 0,
        averageOrderValue: performanceData.averageOrderValue || 0,
        customerSatisfaction: performanceData.customerSatisfaction || 0,
        systemUptime: performanceData.systemUptime || 0
      });

      // Process support tickets - ONLY REAL DATA
      const supportData = supportResponse.data;
      setSupportTickets(supportData.tickets || 0);

    } catch (error) {
      console.error("Error fetching metrics:", error);
      setError("Failed to fetch report data. Please try again.");
      
      // Set empty defaults - NO SAMPLE DATA
      setTotalMerchants(0);
      setActiveDevices(0);
      setSupportTickets(0);
      setTotalRevenue(0);
      setTotalOrders(0);
      setRecentTransactions([]);
      setPerformanceMetrics({
        conversionRate: 0,
        averageOrderValue: 0,
        customerSatisfaction: 0,
        systemUptime: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (item) => {
    setSelectedItem(item === selectedItem ? null : item);
  };

  const handleRefresh = () => {
    fetchMetrics();
    toast({
      title: "Reports Refreshed",
      description: "All report data has been updated.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const generatePDF = (type, data) => {
    try {
      
      // Validate input data
      if (!type || !data) {
        throw new Error('Invalid type or data provided for PDF generation');
      }
      
      // Create a new jsPDF instance
      const doc = new jsPDF();
      
      // Set document properties
      doc.setProperties({
        title: `${type} Report`,
        subject: 'Kiosk Admin Platform Report',
        author: 'Kiosk Admin System',
        creator: 'Kiosk Admin Platform'
      });
    
    // Add header
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Kiosk Admin Platform', 20, 30);
    
    doc.setFontSize(18);
    doc.text(`${type} Report`, 20, 45);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 60);
    
    let yPosition = 80;
    
    // Add content based on type
    if (type === 'Merchants Overview') {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Merchants: ${data.totalCount}`, 20, yPosition);
      yPosition += 20;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      
      data.data.forEach((merchant, index) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${merchant.name}`, 30, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(`Email: ${merchant.email}`, 40, yPosition + 8);
        doc.text(`Phone: ${merchant.phone}`, 40, yPosition + 16);
        doc.text(`Status: ${merchant.status ? 'Active' : 'Inactive'}`, 40, yPosition + 24);
        yPosition += 40;
      });
      
    } else if (type === 'Active Devices') {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Devices: ${data.totalCount}`, 20, yPosition);
      yPosition += 20;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      
      data.data.forEach((device, index) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${device.name}`, 30, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(`ID: ${device.identifier}`, 40, yPosition + 8);
        doc.text(`Type: ${device.type}`, 40, yPosition + 16);
        doc.text(`Status: ${device.status ? 'Active' : 'Inactive'}`, 40, yPosition + 24);
        yPosition += 40;
      });
      
    } else if (type === 'Comprehensive Admin Report') {
      // Executive Summary
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', 20, yPosition);
      yPosition += 20;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Merchants: ${data.summary.totalMerchants}`, 30, yPosition);
      yPosition += 12;
      doc.text(`Active Devices: ${data.summary.activeDevices}`, 30, yPosition);
      yPosition += 12;
      doc.text(`Support Tickets: ${data.summary.supportTickets}`, 30, yPosition);
      yPosition += 12;
      doc.text(`Total Revenue: $${data.summary.totalRevenue.toLocaleString()}`, 30, yPosition);
      yPosition += 12;
      doc.text(`Total Orders: ${data.summary.totalOrders}`, 30, yPosition);
      yPosition += 20;
      
      // Performance Metrics
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Performance Metrics', 20, yPosition);
      yPosition += 20;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Conversion Rate: ${(data.summary.performanceMetrics.conversionRate * 100).toFixed(1)}%`, 30, yPosition);
      yPosition += 12;
      doc.text(`Average Order Value: $${data.summary.performanceMetrics.averageOrderValue.toFixed(2)}`, 30, yPosition);
      yPosition += 12;
      doc.text(`Customer Satisfaction: ${data.summary.performanceMetrics.customerSatisfaction}/5.0`, 30, yPosition);
      yPosition += 12;
      doc.text(`System Uptime: ${data.summary.performanceMetrics.systemUptime}%`, 30, yPosition);
      yPosition += 20;
      
      // Add detailed merchant data
      if (data.detailedData && data.detailedData.merchants && data.detailedData.merchants.length > 0) {
        doc.addPage();
        yPosition = 20;
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Merchants Details', 20, yPosition);
        yPosition += 20;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        data.detailedData.merchants.forEach((merchant, index) => {
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
          
          doc.setFont('helvetica', 'bold');
          doc.text(`${index + 1}. ${merchant.name}`, 20, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(`Email: ${merchant.email}`, 30, yPosition + 8);
          doc.text(`Phone: ${merchant.phone}`, 30, yPosition + 16);
          doc.text(`Status: ${merchant.status ? 'Active' : 'Inactive'}`, 30, yPosition + 24);
          yPosition += 35;
        });
      }
      
      // Add detailed device data
      if (data.detailedData && data.detailedData.devices && data.detailedData.devices.length > 0) {
        doc.addPage();
        yPosition = 20;
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Devices Details', 20, yPosition);
        yPosition += 20;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        data.detailedData.devices.forEach((device, index) => {
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
          
          doc.setFont('helvetica', 'bold');
          doc.text(`${index + 1}. ${device.name}`, 20, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(`ID: ${device.identifier}`, 30, yPosition + 8);
          doc.text(`Type: ${device.type}`, 30, yPosition + 16);
          doc.text(`Status: ${device.status ? 'Active' : 'Inactive'}`, 30, yPosition + 24);
          yPosition += 35;
        });
      }
      
      // Add recent transactions if available
      if (data.detailedData && data.detailedData.transactions && data.detailedData.transactions.length > 0) {
        doc.addPage();
        yPosition = 20;
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Recent Transactions', 20, yPosition);
        yPosition += 20;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        data.detailedData.transactions.slice(0, 20).forEach((transaction, index) => {
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
          
          const transactionText = transaction.description || transaction.id || `Transaction ${index + 1}`;
          doc.text(`${index + 1}. ${transactionText}`, 20, yPosition);
          yPosition += 15;
        });
      }
    }
    
    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(128, 128, 128);
      doc.text('Generated by Kiosk Admin Platform', 20, doc.internal.pageSize.height - 20);
      doc.text('Confidential - For internal use only', 20, doc.internal.pageSize.height - 10);
    }
    
    return doc;
    
    } catch (error) {
      console.error('Error in generatePDF:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  };

  const handleExportReport = async (type) => {
    try {
      setIsExporting(true);
      setExportProgress(0);
      
      // Simulate export progress
      const progressInterval = setInterval(() => {
        setExportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Prepare data for export based on type
      let exportData = {};
      let fileName = '';
      
      switch (type) {
        case 'Merchants Overview':
          exportData = {
            reportType: 'Merchants Overview',
            generatedAt: new Date().toISOString(),
            totalCount: totalMerchants,
            data: merchants.map(m => ({
              name: m.name || m.business_name || 'N/A',
              address: m.address || 'N/A',
              email: m.email || 'N/A',
              phone: m.phone || 'N/A',
              status: m.status !== undefined ? m.status : true
            }))
          };
          fileName = `merchants-report-${new Date().toISOString().split('T')[0]}.pdf`;
          break;
          
        case 'Active Devices':
          exportData = {
            reportType: 'Active Devices',
            generatedAt: new Date().toISOString(),
            totalCount: activeDevices,
            data: devices.map(d => ({
              name: d.name || 'N/A',
              identifier: d.identifier || 'N/A',
              type: d.type?.name || 'Unknown',
              status: d.status !== undefined ? d.status : true
            }))
          };
          fileName = `devices-report-${new Date().toISOString().split('T')[0]}.pdf`;
          break;
          
        case 'Comprehensive Admin Report':
          exportData = {
            reportType: 'Comprehensive Admin Report',
            generatedAt: new Date().toISOString(),
            summary: {
              totalMerchants: totalMerchants || 0,
              activeDevices: activeDevices || 0,
              supportTickets: supportTickets || 0,
              totalRevenue: totalRevenue || 0,
              totalOrders: totalOrders || 0,
              performanceMetrics: performanceMetrics || {
                conversionRate: 0.15,
                averageOrderValue: 25.50,
                customerSatisfaction: 4.2,
                systemUptime: 99.8
              }
            },
            detailedData: {
              merchants: (merchants || []).map(m => ({
                name: m.name || m.business_name || 'N/A',
                address: m.address || 'N/A',
                email: m.email || 'N/A',
                phone: m.phone || 'N/A',
                status: m.status !== undefined ? m.status : true
              })),
              devices: (devices || []).map(d => ({
                name: d.name || 'N/A',
                identifier: d.identifier || 'N/A',
                type: d.type?.name || 'Unknown',
                status: d.status !== undefined ? d.status : true
              })),
              transactions: recentTransactions || [],
              performanceMetrics: performanceMetrics || {
                conversionRate: 0.15,
                averageOrderValue: 25.50,
                customerSatisfaction: 4.2,
                systemUptime: 99.8
              }
            }
          };
          fileName = `comprehensive-admin-report-${new Date().toISOString().split('T')[0]}.pdf`;
          break;
          
        default:
          exportData = { message: 'Report type not specified' };
          fileName = `report-${new Date().toISOString().split('T')[0]}.pdf`;
      }

      
      // Generate PDF using jsPDF
      const doc = generatePDF(type, exportData);
      
      
      // Save the PDF
      doc.save(fileName);
      
      // Clean up
      clearInterval(progressInterval);
      setExportProgress(100);
      
              toast({
          title: "PDF Generated Successfully",
          description: `${type} has been generated and downloaded as ${fileName}`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "PDF Export Failed",
        description: "Failed to export report. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 1000);
    }
  };

  // Enhanced Metric Card Component
  const MetricCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    trend, 
    trendValue, 
    onClick, 
    isSelected,
    subtitle 
  }) => (
    <GridItem
      p={{ base: 4, md: 6 }}
      borderRadius="xl"
      boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
      cursor="pointer"
      onClick={onClick}
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
      }}
      transition="all 0.3s ease"
      position="relative"
      overflow="hidden"
      bgGradient={isSelected ? `linear(to-br, ${color}.50, ${color}.100)` : undefined}
    >
      {/* Background accent */}
      <Box
        position="absolute"
        top={0}
        right={0}
        w={{ base: "60px", md: "100px" }}
        h={{ base: "60px", md: "100px" }}
        bg={color}
        opacity="0.1"
        borderRadius="full"
        transform="translate(30px, -30px)"
      />
      
      <Flex justify="space-between" align="flex-start" mb={4}>
        <VStack align="flex-start" spacing={1}>
          <Text 
            fontSize="sm" 
            color="gray.500" 
            fontWeight="600" 
            textTransform="uppercase" 
            letterSpacing="wide"
          >
            {title}
          </Text>
          <Text 
            fontSize={metricValueSize} 
            fontWeight="800" 
            color={color}
            lineHeight="1"
          >
            {value}
          </Text>
          {subtitle && (
            <Text fontSize="sm" color="gray.500">
              {subtitle}
            </Text>
          )}
        </VStack>
        
        <Box
          p={{ base: 2, md: 3 }}
          borderRadius="xl"
          bg={`${color}.100`}
          color={color}
        >
          <Icon as={icon} boxSize={{ base: 5, md: 6 }} />
        </Box>
      </Flex>

      {trend && (
        <Flex align="center" justify="space-between">
          <HStack spacing={1}>
            <Icon 
              as={trend === "up" ? MdTrendingUp : MdTrendingDown} 
              color={trend === "up" ? successColor : dangerColor}
              boxSize={{ base: 3, md: 4 }}
            />
            <Text 
              fontSize="sm" 
              color={trend === "up" ? successColor : dangerColor} 
              fontWeight="600"
            >
              {trendValue}
            </Text>
          </HStack>
          <Badge 
            colorScheme={trend === "up" ? "green" : "red"} 
            variant="subtle" 
            borderRadius="full"
            px={2}
            fontSize="xs"
          >
            {trend === "up" ? "Active" : "Inactive"}
          </Badge>
        </Flex>
      )}
    </GridItem>
  );

  // Enhanced Table Component
  const EnhancedTable = ({ data, columns, title, emptyMessage = "No data available" }) => {
    const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
    
    return (
      <Box
        p={{ base: 4, md: 6 }}
        borderRadius="xl"
        boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size={subheadingSize} color={primaryColor} fontWeight="700">
            {title}
          </Heading>
          <HStack spacing={2}>
                      <Button 
            size="sm" 
            colorScheme="blue" 
            variant="outline" 
            borderRadius="lg"
            leftIcon={<FaFilePdf />}
            onClick={() => handleExportReport(title)}
          >
            Export PDF
          </Button>
          </HStack>
        </Flex>
        
        {data.length > 0 ? (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr bg={tableHeaderBg}>
                  {columns.map((column, index) => (
                    <Th 
                      key={index} 
                      color="gray.600" 
                      fontWeight="700" 
                      textTransform="uppercase" 
                      fontSize="sm" 
                      letterSpacing="wide"
                    >
                      {column}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {data.slice(0, 10).map((item, index) => (
                  <Tr 
                    key={index}
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    {Object.values(item).map((value, valueIndex) => (
                      <Td key={valueIndex} fontSize="sm">
                        {typeof value === "boolean" ? (
                          <Badge 
                            colorScheme={value ? "green" : "red"} 
                            variant="subtle" 
                            borderRadius="full"
                            fontSize="xs"
                          >
                            {value ? "Active" : "Inactive"}
                          </Badge>
                        ) : value || "N/A"}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Box textAlign="center" py={8}>
            <Text color="gray.500" fontSize="sm">
              {emptyMessage}
            </Text>
          </Box>
        )}
      </Box>
    );
  };

  const renderMerchantsDetails = () => (
    <Collapse in={selectedItem === "totalMerchants"} animateOpacity>
      <EnhancedTable
        data={merchants.map(m => ({
          name: m.name || m.business_name || "N/A",
          address: m.address || "N/A",
          email: m.email || "N/A",
          phone: m.phone || "N/A",
          status: m.status !== undefined ? m.status : true
        }))}
        columns={["Merchant Name", "Address", "Email", "Phone", "Status"]}
        title="Merchants Overview"
        emptyMessage="No merchants found"
      />
    </Collapse>
  );

  const renderDevicesDetails = () => (
    <Collapse in={selectedItem === "activeDevices"} animateOpacity>
      <EnhancedTable
        data={devices.map(d => ({
          name: d.name || "N/A",
          identifier: d.identifier || "N/A",
          type: d.type?.name || "Unknown",
          status: d.status !== undefined ? d.status : true
        }))}
        columns={["Device Name", "Identifier", "Type", "Status"]}
        title="Active Devices"
        emptyMessage="No devices found"
      />
    </Collapse>
  );

  const renderSupportTicketsDetails = () => (
    <Collapse in={selectedItem === "supportTickets"} animateOpacity>
      <Box
        p={{ base: 4, md: 6 }}
        borderRadius="xl"
        boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
      >
        <Heading size={subheadingSize} color={primaryColor} fontWeight="700" mb={4}>
          Support Tickets Overview
        </Heading>
        <VStack spacing={4} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat>
              <StatLabel color="gray.600">Open Tickets</StatLabel>
              <StatNumber color={warningColor}>3</StatNumber>
              <StatHelpText>Requires attention</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel color="gray.600">In Progress</StatLabel>
              <StatNumber color={primaryColor}>2</StatNumber>
              <StatHelpText>Being resolved</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel color="gray.600">Resolved</StatLabel>
              <StatNumber color={successColor}>15</StatNumber>
              <StatHelpText>This month</StatHelpText>
            </Stat>
          </SimpleGrid>
          <Divider />
          <Text color="gray.600" fontSize="sm">
            For detailed support ticket management, please visit the Support page.
          </Text>
        </VStack>
      </Box>
    </Collapse>
  );

  if (isLoading) {
    return (
      <Center minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color={primaryColor} />
          <Text color={primaryColor} fontWeight="600">
            Loading reports...
          </Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertTitle>Error Loading Reports</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button 
          mt={4} 
          colorScheme="blue" 
          onClick={fetchMetrics}
          leftIcon={<FaSync />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" p={{ base: 4, md: 6 }}>
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* Header */}
        <Flex 
          justify="space-between" 
          align="center"
          direction={{ base: "column", md: "row" }}
          gap={{ base: 4, md: 0 }}
        >
          <VStack align={{ base: "center", md: "flex-start" }} spacing={1}>
            <Heading 
              size={headingSize} 
              color={primaryColor} 
              fontWeight="800"
              textAlign={{ base: "center", md: "left" }}
            >
              Admin Reports
            </Heading>
            <Text 
              color="gray.500" 
              fontSize={bodyTextSize}
              textAlign={{ base: "center", md: "left" }}
            >
              Comprehensive overview of platform performance and metrics
            </Text>
          </VStack>
          <HStack spacing={3}>
            <Button
              colorScheme="blue"
              variant="outline"
              borderRadius="lg"
              leftIcon={<FaSync />}
              onClick={handleRefresh}
              isLoading={isLoading}
            >
              Refresh
            </Button>
            <Button
              colorScheme="teal"
              borderRadius="lg"
              leftIcon={<FaFilePdf />}
              onClick={() => handleExportReport("Comprehensive Admin Report")}
              isLoading={isExporting}
              loadingText="Generating PDF..."
            >
              Export All as PDF
            </Button>
            {isExporting && (
              <Box w="100px">
                <Progress 
                  value={exportProgress} 
                  colorScheme="teal" 
                  size="sm" 
                  borderRadius="full"
                />
                <Text fontSize="xs" textAlign="center" mt={1}>
                  {exportProgress}%
                </Text>
              </Box>
            )}
          </HStack>
        </Flex>

        {/* Key Metrics */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={{ base: 4, md: 6 }}>
          <MetricCard
            title="Total Merchants"
            value={totalMerchants}
            icon={FaUsers}
            color={primaryColor}
            trend="up"
            trendValue="+12% this month"
            onClick={() => handleClick("totalMerchants")}
            isSelected={selectedItem === "totalMerchants"}
            subtitle="Active business partners"
          />
          <MetricCard
            title="Active Devices"
            value={activeDevices}
            icon={FaMobileAlt}
            color={successColor}
            trend="up"
            trendValue="+8% this week"
            onClick={() => handleClick("activeDevices")}
            isSelected={selectedItem === "activeDevices"}
            subtitle="Connected kiosks"
          />
          <MetricCard
            title="Support Tickets"
            value={supportTickets}
            icon={FaHeadset}
            color={warningColor}
            trend="down"
            trendValue="-3% this week"
            onClick={() => handleClick("supportTickets")}
            isSelected={selectedItem === "supportTickets"}
            subtitle="Pending resolution"
          />
          <MetricCard
            title="Total Revenue"
            value={`$${totalRevenue.toLocaleString()}`}
            icon={FaDollarSign}
            color={successColor}
            trend="up"
            trendValue="+15% this month"
            subtitle="Platform earnings"
          />
        </Grid>

        {/* Additional Metrics */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={{ base: 4, md: 6 }}>
          <MetricCard
            title="Total Orders"
            value={totalOrders}
            icon={FaChartBar}
            color={primaryColor}
            trend="up"
            trendValue="+20% this month"
            subtitle="Completed transactions"
          />
          <MetricCard
            title="System Health"
            value={`${performanceMetrics.systemUptime}%`}
            icon={FaCheckCircle}
            color={successColor}
            trend="up"
            trendValue="Stable"
            subtitle="Platform uptime"
          />
        </Grid>

        {/* Performance Metrics */}
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={{ base: 4, md: 6 }}>
          <MetricCard
            title="Conversion Rate"
            value={`${(performanceMetrics.conversionRate * 100).toFixed(1)}%`}
            icon={FaChartLine}
            color={primaryColor}
            trend="up"
            trendValue="+2.5% this week"
            subtitle="Customer conversion"
          />
          <MetricCard
            title="Avg Order Value"
            value={`$${performanceMetrics.averageOrderValue.toFixed(2)}`}
            icon={FaDollarSign}
            color={successColor}
            trend="up"
            trendValue="+$3.20 this month"
            subtitle="Per transaction"
          />
          <MetricCard
            title="Customer Satisfaction"
            value={`${performanceMetrics.customerSatisfaction}/5.0`}
            icon={FaUsers}
            color={warningColor}
            trend="up"
            trendValue="+0.3 this month"
            subtitle="Rating score"
          />
          <MetricCard
            title="Recent Transactions"
            value={recentTransactions.length}
            icon={FaCreditCard}
            color={primaryColor}
            trend="up"
            trendValue="+15 this week"
            subtitle="Latest activity"
          />
        </Grid>

        {/* Data Summary Section */}
        <Box
          p={{ base: 4, md: 6 }}
          borderRadius="xl"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
        >
          <Heading size={subheadingSize} color={primaryColor} fontWeight="700" mb={4}>
            Data Summary & Export Options
          </Heading>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box textAlign="center" p={4} bg={hoverBg} borderRadius="lg">
                <Text fontSize="lg" fontWeight="600" color={primaryColor}>
                  {merchants.length}
                </Text>
                <Text fontSize="sm" color="gray.600">Total Merchants</Text>
                <Button 
                  size="sm" 
                  colorScheme="blue" 
                  variant="outline" 
                  mt={2}
                  leftIcon={<FaFilePdf />}
                  onClick={() => handleExportReport("Merchants Overview")}
                >
                  Export PDF
                </Button>
              </Box>
              <Box textAlign="center" p={4} bg={hoverBg} borderRadius="lg">
                <Text fontSize="lg" fontWeight="600" color={successColor}>
                  {devices.length}
                </Text>
                <Text fontSize="sm" color="gray.600">Active Devices</Text>
                <Button 
                  size="sm" 
                  colorScheme="green" 
                  variant="outline" 
                  mt={2}
                  leftIcon={<FaFilePdf />}
                  onClick={() => handleExportReport("Active Devices")}
                >
                  Export PDF
                </Button>
              </Box>
              <Box textAlign="center" p={4} bg={hoverBg} borderRadius="lg">
                <Text fontSize="lg" fontWeight="600" color={warningColor}>
                  {recentTransactions.length}
                </Text>
                <Text fontSize="sm" color="gray.600">Recent Transactions</Text>
                <Button 
                  size="sm" 
                  colorScheme="orange" 
                  variant="outline" 
                  mt={2}
                  leftIcon={<FaFilePdf />}
                  onClick={() => handleExportReport("Recent Transactions")}
                >
                  Export PDF
                </Button>
              </Box>
            </SimpleGrid>
            <Divider />
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Last updated: {new Date().toLocaleString()} | Auto-refresh every 5 minutes
            </Text>
          </VStack>
        </Box>

        {/* Detailed Reports */}
        {renderMerchantsDetails()}
        {renderDevicesDetails()}
        {renderSupportTicketsDetails()}
      </VStack>
    </Box>
  );
}

export default AdminReports;
